/**
 * ReportTableView Component
 *
 * Table view for reports list page. Mirrors the visual design of ClientTableView
 * exactly (rounded-[22px] container, 0.5px borders, hover states, DropdownMenu
 * action menu) but with report-specific columns.
 *
 * Columns: Report, Client, Status, Date Range, Format, Updated, Created, Actions
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  SortAscending,
  SortDescending,
  PencilSimple,
  CopySimple,
  DownloadSimple,
  Trash,
  Slideshow,
  FilePdf,
  CalendarDots,
  PaperPlaneTilt,
  UserPlus,
  CheckCircle,
  ChatText,
  ArrowUUpLeft,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import type { ReviewAction } from "./ReviewActionDialog";

// ============================================================================
// Types
// ============================================================================

export interface ReportListItem {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  clientLogoUrl?: string | null;
  status: "draft" | "ready";
  format: "document" | "slide-deck";
  dateRangeStart: string;
  dateRangeEnd: string;
  pdfUrl: string | null;
  createdAt: number;
  updatedAt: number;
  // Story 24.2 — approval state, ORTHOGONAL to `status` (generation lifecycle).
  // null = never submitted for review. Surfaced as a SEPARATE badge.
  approvalStatus?:
    | "pending_review"
    | "approved"
    | "changes_requested"
    | "withdrawn"
    | null;
  // Story 24.3 — assigned reviewer (null = unassigned); name resolved for display.
  reviewerId?: string | null;
  reviewerName?: string | null;
  reviewerEmail?: string | null;
}

export type ReportSortOption =
  | "name_asc"
  | "name_desc"
  | "client_asc"
  | "client_desc"
  | "status_asc"
  | "status_desc"
  | "format_asc"
  | "format_desc"
  | "date_range_asc"
  | "date_range_desc"
  | "created_desc"
  | "created_asc"
  | "updated_desc"
  | "updated_asc";

interface ReportTableViewProps {
  reports: ReportListItem[];
  canManageReports: boolean;
  onReportClick?: (report: ReportListItem) => void;
  onEditClick?: (report: ReportListItem) => void;
  onDuplicateClick?: (report: ReportListItem) => void;
  onDownloadClick?: (report: ReportListItem) => void;
  onDeleteClick?: (report: ReportListItem) => void;
  onSubmitClick?: (report: ReportListItem) => void;
  onAssignClick?: (report: ReportListItem) => void;
  onReviewClick?: (report: ReportListItem, action: ReviewAction) => void;
  onHistoryClick?: (report: ReportListItem) => void;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  sortBy?: ReportSortOption;
  onSortChange?: (sort: ReportSortOption) => void;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

// ============================================================================
// Helpers (kept in-sync with reports/index.tsx)
// ============================================================================

export const STATUS_STYLES: Record<
  ReportListItem["status"],
  { label: string; className: string }
> = {
  draft: {
    label: "Draft",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  ready: {
    label: "Ready",
    className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
};

// Story 24.2 — approval badge styles. A SEPARATE Record (never folded into
// STATUS_STYLES, which is looked up unguarded over draft|ready). Keyed by the 4
// approval states; `null`/undefined approvalStatus renders no badge.
export const APPROVAL_STATUS_STYLES: Record<
  "pending_review" | "approved" | "changes_requested" | "withdrawn",
  { label: string; className: string }
> = {
  pending_review: {
    label: "Pending review",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  },
  approved: {
    label: "Approved",
    className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
  changes_requested: {
    label: "Changes requested",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  withdrawn: {
    label: "Withdrawn",
    className: "bg-muted text-muted-foreground",
  },
};

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}`;
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ============================================================================
// Component
// ============================================================================

export function ReportTableView({
  reports,
  canManageReports,
  onReportClick,
  onEditClick,
  onDuplicateClick,
  onDownloadClick,
  onDeleteClick,
  onSubmitClick,
  onAssignClick,
  onReviewClick,
  onHistoryClick,
  triggerRefs,
  sortBy,
  onSortChange,
  hiddenColumns = [],
}: ReportTableViewProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div
      data-testid="report-table-view"
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Report list">
        <thead className="relative bg-background">
          <tr>
            {/* Report */}
            <th
              scope="col"
              className="text-left py-2 px-4 text-[13px] font-semibold text-foreground"
            >
              {onSortChange ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() =>
                    onSortChange(sortBy === "name_asc" ? "name_desc" : "name_asc")
                  }
                >
                  <TableHeaderIcon name="report" />
                  Report
                  {(sortBy === "name_asc" || sortBy === "name_desc") &&
                    (sortBy === "name_asc" ? (
                      <SortAscending size={18} />
                    ) : (
                      <SortDescending size={18} />
                    ))}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name="report" />
                  Report
                </span>
              )}
            </th>

            {/* Client */}
            {!isHidden("client") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="client" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "client_asc" ? "client_desc" : "client_asc")
                      }
                    >
                      Client
                      {(sortBy === "client_asc" || sortBy === "client_desc") &&
                        (sortBy === "client_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Client"
                  )}
                </span>
              </th>
            )}

            {/* Status */}
            {!isHidden("status") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="status" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "status_asc" ? "status_desc" : "status_asc")
                      }
                    >
                      Status
                      {(sortBy === "status_asc" || sortBy === "status_desc") &&
                        (sortBy === "status_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Status"
                  )}
                </span>
              </th>
            )}

            {/* Date Range */}
            {!isHidden("dateRange") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="dateRange" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "date_range_asc" ? "date_range_desc" : "date_range_asc"
                        )
                      }
                    >
                      Date Range
                      {(sortBy === "date_range_asc" || sortBy === "date_range_desc") &&
                        (sortBy === "date_range_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Date Range"
                  )}
                </span>
              </th>
            )}

            {/* Format */}
            {!isHidden("format") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="format" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "format_asc" ? "format_desc" : "format_asc")
                      }
                    >
                      Format
                      {(sortBy === "format_asc" || sortBy === "format_desc") &&
                        (sortBy === "format_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Format"
                  )}
                </span>
              </th>
            )}

            {/* Updated */}
            {!isHidden("updated") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="updated" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "updated_desc" ? "updated_asc" : "updated_desc")
                      }
                    >
                      Updated
                      {(sortBy === "updated_asc" || sortBy === "updated_desc") &&
                        (sortBy === "updated_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Updated"
                  )}
                </span>
              </th>
            )}

            {/* Created */}
            {!isHidden("created") && (
              <th
                scope="col"
                className="group w-px text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="created" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(sortBy === "created_desc" ? "created_asc" : "created_desc")
                      }
                    >
                      Created
                      {(sortBy === "created_asc" || sortBy === "created_desc") &&
                        (sortBy === "created_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Created"
                  )}
                </span>
              </th>
            )}

            {canManageReports && (
              <th scope="col" className="py-2 px-4 w-16">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const FormatIcon = report.format === "slide-deck" ? Slideshow : FilePdf;
            const statusStyle = STATUS_STYLES[report.status];

            return (
              <tr
                key={report.id}
                data-testid="report-row"
                className={`bg-background hover:bg-brand/[0.02] transition-colors${onReportClick ? " cursor-pointer" : ""}`}
                onClick={() => onReportClick?.(report)}
                onKeyDown={(e) => {
                  if (onReportClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onReportClick(report);
                  }
                }}
                tabIndex={onReportClick ? 0 : undefined}
              >
                {/* Report */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium truncate">{report.name}</p>
                  </div>
                </td>

                {/* Client */}
                {!isHidden("client") && (
                  <td className="py-3 px-4 text-foreground hidden md:table-cell whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <ClientAvatar
                        name={report.clientName}
                        logoUrl={report.clientLogoUrl ?? null}
                        size="sm"
                      />
                      <span className="truncate">{report.clientName}</span>
                    </div>
                  </td>
                )}

                {/* Status (generation lifecycle + orthogonal approval badge) */}
                {!isHidden("status") && (
                  <td className="py-3 px-4 whitespace-nowrap">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle.className}`}
                      >
                        {statusStyle.label}
                      </span>
                      {report.approvalStatus &&
                        APPROVAL_STATUS_STYLES[report.approvalStatus] && (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${APPROVAL_STATUS_STYLES[report.approvalStatus].className}`}
                          >
                            {APPROVAL_STATUS_STYLES[report.approvalStatus].label}
                          </span>
                        )}
                      {report.reviewerId &&
                        (report.reviewerName || report.reviewerEmail) && (
                          <span className="text-xs text-muted-foreground">
                            · {report.reviewerName ?? report.reviewerEmail}
                          </span>
                        )}
                    </div>
                  </td>
                )}

                {/* Date Range */}
                {!isHidden("dateRange") && (
                  <td className="py-3 px-4 text-foreground whitespace-nowrap hidden lg:table-cell">
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDots size={16} />
                      {formatDateRange(report.dateRangeStart, report.dateRangeEnd)}
                    </span>
                  </td>
                )}

                {/* Format */}
                {!isHidden("format") && (
                  <td className="py-3 px-4 hidden md:table-cell whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 text-foreground">
                      <FormatIcon size={16} />
                      {report.format === "slide-deck" ? "Slide Deck" : "Document"}
                    </span>
                  </td>
                )}

                {/* Updated */}
                {!isHidden("updated") && (
                  <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                    {formatTimestamp(report.updatedAt)}
                  </td>
                )}

                {/* Created */}
                {!isHidden("created") && (
                  <td className="w-px py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                    {formatTimestamp(report.createdAt)}
                  </td>
                )}

                {canManageReports && (
                  <td
                    className="py-3 px-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          ref={(el) => {
                            triggerRefs.current[report.id] = el;
                          }}
                          variant="ghost"
                          size="icon"
                          className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                          aria-label={`Actions for ${report.name}`}
                        >
                          <DotsThreeVertical size={28} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="rounded-[23px] p-2 shadow-md"
                        style={{ border: "0.5px solid var(--hairline)" }}
                      >
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onEditClick?.(report)}
                        >
                          <PencilSimple size={14} className="mr-1" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onSubmitClick?.(report)}
                        >
                          <PaperPlaneTilt size={14} className="mr-1" />
                          {report.approvalStatus === "changes_requested" ||
                          report.approvalStatus === "withdrawn"
                            ? "Resubmit for review"
                            : "Submit for review"}
                        </DropdownMenuItem>
                        {report.approvalStatus && (
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onAssignClick?.(report)}
                          >
                            <UserPlus size={14} className="mr-1" />
                            Assign reviewer
                          </DropdownMenuItem>
                        )}
                        {report.approvalStatus === "pending_review" && (
                          <>
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onReviewClick?.(report, "approve")}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onReviewClick?.(report, "request_changes")}
                            >
                              <ChatText size={14} className="mr-1" />
                              Request changes
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                              onClick={() => onReviewClick?.(report, "withdraw")}
                            >
                              <ArrowUUpLeft size={14} className="mr-1" />
                              Withdraw
                            </DropdownMenuItem>
                          </>
                        )}
                        {report.approvalStatus && (
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onHistoryClick?.(report)}
                          >
                            <ClockCounterClockwise size={14} className="mr-1" />
                            Approval history
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onDuplicateClick?.(report)}
                        >
                          <CopySimple size={14} className="mr-1" />
                          Duplicate
                        </DropdownMenuItem>
                        {report.pdfUrl && (
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onDownloadClick?.(report)}
                          >
                            <DownloadSimple size={14} className="mr-1" />
                            Download PDF
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                          onClick={() => onDeleteClick?.(report)}
                        >
                          <Trash size={14} className="mr-1" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
